import React, { useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import api from '../api/axiosConfig';

Modal.setAppElement('#root');

const Wrap = styled.div`
  padding: 22px;
`;

const Title = styled.h2`
  margin: 0;
  color: #053969;
`;

const ProductInfo = styled.div`
  margin: 10px 0 18px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(5, 57, 105, 0.06);
  color: #0b1b2b;
  font-weight: 700;
`;

const Row = styled.div`
  display: grid;
  gap: 10px;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  outline: none;

  &:focus {
    border-color: rgba(5, 57, 105, 0.6);
  }
`;

const Note = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.65);
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 13px;
  line-height: 1.4;
  cursor: pointer;

  input {
    margin-top: 3px;
  }
`;

const ErrorText = styled.div`
  color: #b42318;
  font-size: 13px;
  font-weight: 700;
`;

const Buttons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 16px;
`;

const Btn = styled.button`
  border: none;
  cursor: pointer;
  font-weight: 800;
  padding: 12px 16px;
  border-radius: 999px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BtnClose = styled(Btn)`
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.2);
`;

const BtnSend = styled(Btn)`
  background: #ff8a1f;
  color: #111;
`;

const emptyForm = {
  business_name: '',
  vat_number: '',
  email: '',
  domain: '',
  referral_number: '',
  termsAccepted: false,
  digitalAccepted: false,
};

export default function PurchaseModal({ open, product, onClose, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (isSending) return;
    setError('');
    onClose?.();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!product || isSending) return;

    if (!form.termsAccepted || !form.digitalAccepted) {
      setError('Please accept both confirmations before continuing.');
      return;
    }

    try {
      setIsSending(true);
      setError('');

      const res = await api.post('/orders/create/', {
        product_id: product.id,
        business_name: form.business_name,
        vat_number: form.vat_number,
        email: form.email,
        domain: form.domain,
        referral_number: form.referral_number,
        terms_accepted: form.termsAccepted,
        digital_terms_accepted: form.digitalAccepted,
      });

      setForm(emptyForm);
      onCreated?.(res.data);
      onClose?.();
    } catch (err) {
      if (err?.response?.status === 401) {
        setError('Please login before purchasing.');
      } else {
        const data = err?.response?.data;
        const message =
          data?.detail ||
          Object.values(data || {}).flat().join(' ') ||
          'Failed to create order.';
        setError(String(message));
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={handleClose}
      contentLabel="Purchase"
      style={{
        overlay: { backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 3000 },
        content: {
          maxWidth: '560px',
          width: 'calc(100% - 24px)',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          margin: 'auto',
          borderRadius: '22px',
          padding: '0',
          border: 'none',
          inset: '50% auto auto 50%',
          transform: 'translate(-50%, -50%)',
        },
      }}
    >
      <Wrap>
        <Title>Business checkout</Title>

        <ProductInfo>
          {product?.title || 'Product'}
          {product?.price_from_eur != null ? ` — €${product.price_from_eur}` : ''}
        </ProductInfo>

        <form onSubmit={handleSubmit}>
          <Row>
            <Note>
              Sales are currently available to businesses in Italy only.
            </Note>

            <Input
              name="business_name"
              placeholder="Ragione sociale / Nome attività"
              required
              maxLength={160}
              value={form.business_name}
              onChange={handleChange}
            />

            <Input
              name="vat_number"
              placeholder="Partita IVA"
              required
              maxLength={32}
              value={form.vat_number}
              onChange={handleChange}
            />

            <Input
              name="email"
              type="email"
              placeholder="Email"
              required
              value={form.email}
              onChange={handleChange}
            />

            <Input
              name="domain"
              placeholder="Dominio del sito, es. example.it"
              required
              maxLength={255}
              value={form.domain}
              onChange={handleChange}
            />

            <Input
              name="referral_number"
              placeholder="Referral number (optional)"
              maxLength={100}
              value={form.referral_number}
              onChange={handleChange}
            />

            <CheckLabel>
              <input
                type="checkbox"
                name="termsAccepted"
                checked={form.termsAccepted}
                onChange={handleChange}
              />
              <span>I have read and accept the Terms and License conditions.</span>
            </CheckLabel>

            <CheckLabel>
              <input
                type="checkbox"
                name="digitalAccepted"
                checked={form.digitalAccepted}
                onChange={handleChange}
              />
              <span>
                I understand that this is a digital B2B purchase and that Moloman does not
                offer voluntary refunds, without prejudice to rights that cannot legally be
                excluded.
              </span>
            </CheckLabel>

            {error && <ErrorText>{error}</ErrorText>}
          </Row>

          <Buttons>
            <BtnClose type="button" onClick={handleClose} disabled={isSending}>
              Close
            </BtnClose>

            <BtnSend type="submit" disabled={isSending}>
              {isSending ? 'Creating…' : 'Continue'}
            </BtnSend>
          </Buttons>
        </form>
      </Wrap>
    </Modal>
  );
}
